import { useTranslation } from "react-i18next"

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"
import type { MissionType } from "@/types/missions"

/**
 * MissionTypeSelector — three-card radio group for the OKS-29 New / Edit
 * mission form (UX Spec §2 § Type selector).
 *
 * The cards (Drop / Promotional / Open) are the foundational lever in the
 * mission form: type drives which fields show downstream (promotional
 * reveals `promo_code` + `ecommerce_url`). UX Spec §1: "below the
 * selector, the rest of the form fades in."
 *
 * **A11y (UX Spec §6):**
 *   - Real `<RadioGroup>` (radix) — arrow-key navigable, Tab focus on the
 *     selected card, `aria-checked` reflects state.
 *   - Color is paired with text + a visible border on selection — color
 *     alone is never the selection signal.
 *   - Disabled when `type_locked` (collabs exist post-OKS-30); a tooltip
 *     surfaces the lock reason via `disabled-reason`.
 *
 * Wire shape: emits `mission[mission_type]` via the parent form's
 * controlled `value` (Inertia <Form> render-prop pattern). The card
 * itself is a label wrapping `<RadioGroupItem>` so click-anywhere selects.
 */
interface MissionTypeSelectorProps {
  /** Current selection — null when nothing picked yet (New page). */
  value: MissionType | null
  /** Called with the new selection. Parent owns the dialog-confirm flow. */
  onChange: (next: MissionType) => void
  /** When true, all three cards become non-interactive + show a lock cue. */
  locked?: boolean
  /** Field name for the hidden input (defaults to `mission[mission_type]`). */
  name?: string
  /** Test ID for the wrapper. */
  testId?: string
}

const TYPES: readonly MissionType[] = ["drop", "promotional", "open"] as const

export function MissionTypeSelector({
  value,
  onChange,
  locked = false,
  name = "mission[mission_type]",
  testId = "mission-type-selector",
}: MissionTypeSelectorProps) {
  const { t } = useTranslation()

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <fieldset
        aria-disabled={locked || undefined}
        aria-label={t("missions.form.type_selector.label")}
        className="flex flex-col gap-3"
      >
        <legend className="text-foreground text-sm font-medium">
          {t("missions.form.type_selector.label")}
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        </legend>
        <p className="text-muted-foreground text-xs">
          {t("missions.form.type_selector.helper")}
        </p>

        <RadioGroup
          name={name}
          value={value ?? undefined}
          onValueChange={(next) => {
            if (locked) return
            onChange(next as MissionType)
          }}
          disabled={locked}
          className="grid gap-3 sm:grid-cols-3"
        >
          {TYPES.map((type) => (
            <TypeCard
              key={type}
              type={type}
              selected={value === type}
              locked={locked}
            />
          ))}
        </RadioGroup>

        {locked && (
          <p
            role="status"
            className="text-muted-foreground text-xs"
            data-testid="mission-type-selector-locked"
          >
            {t("missions.form.type_locked.notice")}
          </p>
        )}
      </fieldset>
    </div>
  )
}

interface TypeCardProps {
  type: MissionType
  selected: boolean
  locked: boolean
}

function TypeCard({ type, selected, locked }: TypeCardProps) {
  const { t } = useTranslation()
  const id = `mission-type-${type}`

  return (
    <label
      htmlFor={id}
      data-testid={`mission-type-card-${type}`}
      data-selected={selected ? "true" : "false"}
      className={cn(
        "relative flex cursor-pointer flex-col gap-1 rounded-lg border-2 p-4 transition-colors",
        // Selected card pairs accent fill + thicker border (UX Spec §2).
        // Color is supported by border thickness + the radio dot — never
        // color-alone (a11y).
        selected
          ? "border-primary bg-primary/5 ring-primary/30 ring-2"
          : "border-input bg-background hover:bg-accent",
        locked && "cursor-not-allowed opacity-60",
      )}
    >
      <div className="flex items-center justify-between">
        <span
          id={`${id}-title`}
          className="text-foreground text-sm font-semibold"
        >
          {t(`missions.form.mission_type.${type}.title`)}
        </span>
        {/*
          ⚠️ `aria-labelledby` is REQUIRED here, and it must point at a REAL id.

          The whole card is the `<label>` (click-anywhere + the ≥44px touch
          target), so Base UI's native-label fallback would otherwise pull the
          card's ENTIRE text — title *and* helper — into the accessible name,
          while `aria-describedby` simultaneously declares the helper to be the
          description. The same sentence would sit in the tree twice.

          Pointing `aria-labelledby` at the title span narrows the name to the
          title and leaves the helper as the description. Measured: an explicit
          `aria-labelledby` REPLACES Base UI's generated `${id}-label` (it is
          not appended to it).

          🔴 If this id ever stops resolving — renamed, typo'd, span removed —
          the name silently becomes EMPTY. It does NOT fall back to the label.
          `mission-type-selector.test.tsx` asserts the full accessible name for
          exactly this reason; keep those assertions if you touch this markup.
        */}
        <RadioGroupItem
          id={id}
          value={type}
          disabled={locked}
          aria-labelledby={`${id}-title`}
          aria-describedby={`${id}-helper`}
        />
      </div>
      <p
        id={`${id}-helper`}
        className="text-muted-foreground text-xs leading-tight"
      >
        {t(`missions.form.mission_type.${type}.helper`)}
      </p>
    </label>
  )
}
